import { withCredentials } from "../apiClient";
import { arrayBufferToBase64 } from "../../utils/base64";
import type { ReferenceSlideImage, ToolCall, ToolResult } from "../../types";
import type { DeckPlan, LibrarySlideCandidate, SlideOutline } from "./deckPlannerService";
import type { AgentToolContext } from "./types";
import { fetchSlideLibraryCollections, fetchSlideLibraryCollectionSlides } from "../../slideLibrary/be/endpoints/collections";
import {
  fetchCompanySlideCollections,
  fetchCompanySlideCollectionSlides,
} from "../../slideLibrary/be/endpoints/companyCollections";
import { fetchSlideLibrarySlides } from "../../slideLibrary/be/endpoints/slides";
import type { SlideLibraryCollection, SlideLibrarySlide, SlideLibrarySource } from "../../slideLibrary/be/types";
import { filterInsertableCollections, isInsertableLibrarySlide } from "../../slideLibrary/be/preslinkPolicy";
import { buildLibraryThumbnailSrc } from "../../slideLibrary/ui/components/SlideCards";
import { buildLibraryPptxSrcCandidates } from "../../slideLibrary/ui/host/insertionService";

export const COMPANY_DEFAULT_COLLECTION_NAME = "Company default collection";
export const SLIDE_LIBRARY_SEARCH_DEFAULT_LIMIT = 100;
export const SLIDE_LIBRARY_SEARCH_MAX_LIMIT = 200;
export const ALL_SLIDES_COLLECTION_ID = "__all__";
const COLLECTION_PAGE_SIZE = 100;
const SLIDE_PAGE_SIZE = 100;
const READY_STATUS = "READY";

// Local `Promise.allSettled` — FactSet Workstation's embedded Chromium
// predates ES2020, and rspack's `es6-promise` ProvidePlugin has no
// `allSettled`. Calling the global throws `Promise.allSettled is not a
// function` (search then looks like an empty catalog). Inline it the
// same way ExcelApp does rather than depending on the citation-dialog
// polyfill, which never loads in the taskpane.
type AllSettledResult<T> =
  | { status: "fulfilled"; value: T }
  | { status: "rejected"; reason: unknown };

function allSettled<T>(promises: Array<Promise<T>>): Promise<Array<AllSettledResult<T>>> {
  return Promise.all(
    promises.map((promise) =>
      promise.then(
        (value): AllSettledResult<T> => ({ status: "fulfilled", value }),
        (reason): AllSettledResult<T> => ({ status: "rejected", reason })
      )
    )
  );
}

export interface SlideLibraryCollectionListItem {
  collection_id: string;
  name: string;
  description: string;
  source: SlideLibrarySource;
  isDefault: boolean;
  slide_count?: number;
  status?: string;
  kind?: "standard" | "preslink_template";
}

export interface SlideLibrarySearchItem {
  slide_id: string;
  title: string;
  description: string;
  tags: string[];
  favourite: boolean;
  status: string;
  collection_id: string;
  source: SlideLibrarySource;
  collection_ids?: string[];
  thumbnailUrl?: string;
}

export interface ResolvedSlideLibraryCollection {
  collection_id: string;
  name: string;
  source: SlideLibrarySource;
  isDefault: boolean;
  kind?: "standard" | "preslink_template";
}

export const ALL_SLIDES_COLLECTION: ResolvedSlideLibraryCollection = {
  collection_id: ALL_SLIDES_COLLECTION_ID,
  name: "All slides",
  source: "personal",
  isDefault: false,
};

function normalizeName(value: string | undefined): string {
  return (value || "").trim().toLowerCase();
}

function readSlideId(slide: SlideLibrarySlide): string {
  return (slide.slide_id || slide.slideId || "").trim();
}

function isReadySlide(slide: SlideLibrarySlide): boolean {
  const status = (slide.status || "").trim().toUpperCase();
  return !status || status === READY_STATUS;
}

function toSource(value: SlideLibrarySource | undefined, fallback: SlideLibrarySource): SlideLibrarySource {
  return value === "company" || value === "personal" ? value : fallback;
}

export function isCompanyDefaultCollectionName(name: string): boolean {
  return normalizeName(name) === normalizeName(COMPANY_DEFAULT_COLLECTION_NAME);
}

export function resolveCompanyDefaultCollection(
  collections: SlideLibraryCollectionListItem[]
): SlideLibraryCollectionListItem | null {
  const company = collections.filter(
    (collection) => collection.source === "company" && collection.kind === "standard"
  );
  if (company.length === 0) return null;
  return company.find((collection) => isCompanyDefaultCollectionName(collection.name)) ?? company[0];
}

export function markDefaultCollections(
  collections: SlideLibraryCollectionListItem[]
): SlideLibraryCollectionListItem[] {
  const defaultCollection = resolveCompanyDefaultCollection(collections);
  return collections.map((collection) => ({
    ...collection,
    isDefault: Boolean(defaultCollection && collection.collection_id === defaultCollection.collection_id),
  }));
}

export function matchesSlideLibraryTags(
  slide: Pick<SlideLibrarySlide, "tags">,
  tags?: string[]
): boolean {
  if (!tags || tags.length === 0) return true;
  const slideTags = new Set((slide.tags || []).map((tag) => normalizeName(tag)));
  return tags.some((tag) => slideTags.has(normalizeName(tag)));
}

function toListItem(
  collection: SlideLibraryCollection,
  source: SlideLibrarySource
): SlideLibraryCollectionListItem {
  return {
    collection_id: collection.collection_id,
    name: collection.name,
    description: collection.description || "",
    source,
    isDefault: false,
    slide_count: collection.slide_count ?? collection.slideCount,
    status: collection.status,
    kind: collection.kind,
  };
}

function toSearchItem(
  slide: SlideLibrarySlide,
  collection: ResolvedSlideLibraryCollection,
  collectionIds?: string[]
): SlideLibrarySearchItem {
  const thumbnailUrl = buildLibraryThumbnailSrc(slide) ?? undefined;
  const memberships =
    collectionIds && collectionIds.length > 1 ? collectionIds : undefined;
  return {
    slide_id: readSlideId(slide),
    title: slide.title || "Untitled slide",
    description: slide.description || "",
    tags: slide.tags || [],
    favourite: Boolean(slide.favourite),
    status: slide.status,
    collection_id: collection.collection_id,
    source: collection.source,
    ...(memberships ? { collection_ids: memberships } : {}),
    ...(thumbnailUrl ? { thumbnailUrl } : {}),
  };
}

function ensureLibraryMaps(ctx?: AgentToolContext): {
  cache: Map<string, SlideLibrarySlide>;
  sources: Map<string, { title?: string; pptxSources: string[] }>;
} {
  const cache = ctx?.librarySlideCache ?? new Map<string, SlideLibrarySlide>();
  const sources =
    ctx?.librarySlideSourcesById ?? new Map<string, { title?: string; pptxSources: string[] }>();
  if (ctx) {
    ctx.librarySlideCache = cache;
    ctx.librarySlideSourcesById = sources;
  }
  return { cache, sources };
}

function rememberLibrarySlide(ctx: AgentToolContext | undefined, slide: SlideLibrarySlide): void {
  if (!isInsertableLibrarySlide(slide)) return;
  const slideId = readSlideId(slide);
  if (!slideId) return;
  const { cache, sources } = ensureLibraryMaps(ctx);
  cache.set(slideId, slide);
  const pptxSources = buildLibraryPptxSrcCandidates(slide);
  if (pptxSources.length > 0) {
    sources.set(slideId, { title: slide.title, pptxSources });
  }
}

async function fetchAllCollectionPages(
  fetcher: (page: number) => Promise<{ collections: SlideLibraryCollection[] }>
): Promise<SlideLibraryCollection[]> {
  const collections: SlideLibraryCollection[] = [];
  const seen = new Set<string>();
  let page = 1;
  while (true) {
    const response = await fetcher(page);
    if (response.collections.length === 0) break;
    for (const collection of response.collections) {
      if (!collection.collection_id || seen.has(collection.collection_id)) continue;
      seen.add(collection.collection_id);
      collections.push(collection);
    }
    if (response.collections.length < COLLECTION_PAGE_SIZE) break;
    page += 1;
  }
  return collections;
}

async function fetchAllSlidePages(
  fetcher: (page: number) => Promise<{ slides: SlideLibrarySlide[] }>
): Promise<SlideLibrarySlide[]> {
  const slides: SlideLibrarySlide[] = [];
  const seen = new Set<string>();
  let page = 1;
  while (true) {
    const response = await fetcher(page);
    if (response.slides.length === 0) break;
    for (const slide of response.slides) {
      const slideId = readSlideId(slide);
      if (!slideId || seen.has(slideId) || !isReadySlide(slide)) continue;
      seen.add(slideId);
      slides.push(slide);
    }
    if (response.slides.length < SLIDE_PAGE_SIZE) break;
    page += 1;
  }
  return slides;
}

function catalogFetchErrorMessage(error: unknown, tool: string): string {
  const detail = error instanceof Error ? error.message : String(error);
  return (
    `Could not load company slide collections (${detail}). Do not treat this as an empty library ` +
    `and do not plan FREEFORM. Retry ${tool} or tell the user the catalog failed to load.`
  );
}

function personalLibraryFetchErrorMessage(error: unknown): string {
  const detail = error instanceof Error ? error.message : String(error);
  return (
    `Could not load the personal slide library (${detail}). Do not treat this as an empty library ` +
    `and do not plan FREEFORM. Retry search_slide_library or tell the user the catalog failed to load.`
  );
}

function readSlideCollectionId(slide: SlideLibrarySlide): string {
  return (slide.collection_id || slide.collectionId || "").trim();
}

function remapAllSlidesMembership(entry: {
  slide: SlideLibrarySlide;
  collection: ResolvedSlideLibraryCollection;
  collectionIds: string[];
}): {
  slide: SlideLibrarySlide;
  collection: ResolvedSlideLibraryCollection;
  collectionIds: string[];
} {
  const realId = readSlideCollectionId(entry.slide);
  if (entry.collection.collection_id !== ALL_SLIDES_COLLECTION_ID) {
    return entry;
  }
  if (!realId || realId === ALL_SLIDES_COLLECTION_ID) {
    return entry;
  }
  const collectionIds = uniqueCollectionIds(
    entry.collectionIds.map((id) => (id === ALL_SLIDES_COLLECTION_ID ? realId : id))
  );
  return {
    slide: entry.slide,
    collection: { ...ALL_SLIDES_COLLECTION, collection_id: realId },
    collectionIds,
  };
}

function orderedCompanyCollections(
  collections: SlideLibraryCollectionListItem[]
): ResolvedSlideLibraryCollection[] {
  const company = collections.filter(
    (collection) => collection.source === "company" && collection.kind === "standard"
  );
  const defaultCollection = resolveCompanyDefaultCollection(collections);
  if (!defaultCollection) {
    return company.map((collection) => toResolvedCollection(collection));
  }
  return [
    toResolvedCollection(defaultCollection, true),
    ...company
      .filter((collection) => collection.collection_id !== defaultCollection.collection_id)
      .map((collection) => toResolvedCollection(collection)),
  ];
}

function pageMergedLibrarySearch(input: {
  call: ToolCall;
  ctx?: AgentToolContext;
  loaded: Array<{ collection: ResolvedSlideLibraryCollection; slides: SlideLibrarySlide[] }>;
  offset: number;
  limit: number;
  missingCollectionIds?: string[];
  failedCollectionIds?: string[];
  failedPersonalLibrary?: boolean;
  scope?: "all";
  autoFallback?: boolean;
}): { result: ToolResult } {
  const {
    call,
    ctx,
    loaded,
    offset,
    limit,
    missingCollectionIds,
    failedCollectionIds,
    failedPersonalLibrary,
    scope,
    autoFallback,
  } = input;
  const merged = mergeSlidesAcrossCollections(loaded)
    .filter((entry) => isInsertableLibrarySlide(entry.slide))
    .map(remapAllSlidesMembership);
  for (const entry of merged) {
    rememberLibrarySlide(ctx, entry.slide);
  }

  const filtered = merged.filter((entry) => matchesSlideLibraryTags(entry.slide, call.tags));
  const page = filtered.slice(offset, offset + limit);
  const hasMore = offset + page.length < filtered.length;
  if (ctx && filtered.length > 0) {
    ctx.libraryCatalogHadSlides = true;
  }
  const collectionNoun = loaded.length === 1 ? "This collection has" : "These collections have";
  const emptyMessage =
    filtered.length === 0
      ? call.tags && call.tags.length > 0
        ? `${collectionNoun} no READY slides matching those tags. Paginate without tags, or build from scratch (FREEFORM) and tell the user.`
        : `${collectionNoun} no READY slides. Build from scratch (FREEFORM) and tell the user.`
      : autoFallback
        ? "No company default collection is available. Results are from all insertable library slides (company collections and the personal library)."
        : failedCollectionIds && failedCollectionIds.length > 0
          ? "Some collections failed to load; results are from the collections that loaded."
          : failedPersonalLibrary
            ? "The personal library failed to load; results are from the company collections that loaded."
            : undefined;

  const resultCollections = loaded.map((batch) => batch.collection);
  return {
    result: {
      tool: "search_slide_library",
      success: true,
      data: {
        ...(scope ? { scope } : {}),
        collections: resultCollections,
        tags: call.tags ?? [],
        offset,
        limit,
        returned: page.length,
        totalCount: filtered.length,
        hasMore,
        nextOffset: hasMore ? offset + page.length : null,
        items: page.map((entry) => toSearchItem(entry.slide, entry.collection, entry.collectionIds)),
        ...(missingCollectionIds && missingCollectionIds.length > 0 ? { missingCollectionIds } : {}),
        ...(failedCollectionIds && failedCollectionIds.length > 0 ? { failedCollectionIds } : {}),
        ...(failedPersonalLibrary ? { failedPersonalLibrary: true } : {}),
        ...(emptyMessage ? { message: emptyMessage } : {}),
      },
    },
  };
}

export async function listSlideLibraryCollections(
  companyId?: string
): Promise<SlideLibraryCollectionListItem[]> {
  const [personal, company] = await Promise.all([
    fetchAllCollectionPages((page) =>
      fetchSlideLibraryCollections({ page, pageSize: COLLECTION_PAGE_SIZE })
    ).catch(() => [] as SlideLibraryCollection[]),
    companyId
      ? fetchAllCollectionPages((page) =>
          fetchCompanySlideCollections(companyId, { page, pageSize: COLLECTION_PAGE_SIZE })
        )
      : Promise.resolve([] as SlideLibraryCollection[]),
  ]);

  const items = [
    ...filterInsertableCollections(company).map((collection) => toListItem(collection, "company")),
    ...personal.map((collection) => toListItem(collection, toSource(collection.source, "personal"))),
  ];
  return markDefaultCollections(items);
}

function toResolvedCollection(
  collection: SlideLibraryCollectionListItem,
  isDefault = collection.isDefault
): ResolvedSlideLibraryCollection {
  return {
    collection_id: collection.collection_id,
    name: collection.name,
    source: collection.source,
    isDefault,
    kind: collection.kind,
  };
}

function uniqueCollectionIds(ids: string[] | undefined): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const id of ids ?? []) {
    const trimmed = id.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    unique.push(trimmed);
  }
  return unique;
}

function requestedCollectionIds(call: ToolCall): string[] {
  return uniqueCollectionIds([...(call.collectionIds ?? []), ...(call.collectionId ? [call.collectionId] : [])]);
}

function resolveSearchCollections(
  collections: SlideLibraryCollectionListItem[],
  collectionIds?: string[]
): {
  resolved: ResolvedSlideLibraryCollection[];
  missingCollectionIds: string[];
  message?: string;
} {
  const requested = uniqueCollectionIds(collectionIds);
  if (requested.length === 0) {
    const defaultCollection = resolveCompanyDefaultCollection(collections);
    if (!defaultCollection) {
      return {
        resolved: [],
        missingCollectionIds: [],
        message:
          "No READY company default collection is available. Build slides FREEFORM (omit librarySlideId) and tell the user you will build from scratch.",
      };
    }
    return {
      resolved: [toResolvedCollection(defaultCollection, true)],
      missingCollectionIds: [],
    };
  }

  const byId = new Map(collections.map((collection) => [collection.collection_id, collection]));
  const resolved: ResolvedSlideLibraryCollection[] = [];
  const missingCollectionIds: string[] = [];
  for (const collectionId of requested) {
    const named = byId.get(collectionId);
    if (!named) {
      missingCollectionIds.push(collectionId);
      continue;
    }
    resolved.push(toResolvedCollection(named));
  }
  if (resolved.length === 0) {
    return {
      resolved: [],
      missingCollectionIds,
      message:
        requested.length === 1
          ? "Named collection was not found. Build those slides FREEFORM (omit librarySlideId). Do not silently search the personal library unless the user named that collection."
          : "None of the named collections were found. Build those slides FREEFORM (omit librarySlideId). Do not silently search the personal library unless the user named that collection.",
    };
  }
  return { resolved, missingCollectionIds };
}

function mergeSlidesAcrossCollections(
  batches: Array<{ collection: ResolvedSlideLibraryCollection; slides: SlideLibrarySlide[] }>
): Array<{
  slide: SlideLibrarySlide;
  collection: ResolvedSlideLibraryCollection;
  collectionIds: string[];
}> {
  const order: string[] = [];
  const byId = new Map<
    string,
    {
      slide: SlideLibrarySlide;
      collection: ResolvedSlideLibraryCollection;
      collectionIds: string[];
    }
  >();
  for (const batch of batches) {
    for (const slide of batch.slides) {
      const slideId = readSlideId(slide);
      if (!slideId) continue;
      const existing = byId.get(slideId);
      if (!existing) {
        byId.set(slideId, {
          slide,
          collection: batch.collection,
          collectionIds: [batch.collection.collection_id],
        });
        order.push(slideId);
        continue;
      }
      if (!existing.collectionIds.includes(batch.collection.collection_id)) {
        existing.collectionIds.push(batch.collection.collection_id);
      }
    }
  }
  return order.map((slideId) => byId.get(slideId)!);
}

async function fetchCollectionSlides(
  collection: ResolvedSlideLibraryCollection,
  companyId?: string
): Promise<SlideLibrarySlide[]> {
  if (collection.source === "company") {
    if (!companyId) return [];
    return fetchAllSlidePages((page) =>
      fetchCompanySlideCollectionSlides(collection.collection_id, {
        page,
        pageSize: SLIDE_PAGE_SIZE,
      }, {}, collection.kind)
    );
  }
  return fetchAllSlidePages((page) =>
    fetchSlideLibraryCollectionSlides(collection.collection_id, {
      page,
      pageSize: SLIDE_PAGE_SIZE,
    })
  );
}

export async function executeListSlideLibraryCollectionsTool(
  options: { agentToolContext?: AgentToolContext } = {}
): Promise<{ result: ToolResult }> {
  let collections: SlideLibraryCollectionListItem[];
  try {
    collections = await listSlideLibraryCollections(options.agentToolContext?.companyId);
  } catch (error) {
    return {
      result: {
        tool: "list_slide_library_collections",
        success: false,
        error: catalogFetchErrorMessage(error, "list_slide_library_collections"),
      },
    };
  }
  const defaultCollection = resolveCompanyDefaultCollection(collections);
  return {
    result: {
      tool: "list_slide_library_collections",
      success: true,
      data: {
        collections,
        defaultCollectionId: defaultCollection?.collection_id ?? null,
        message: defaultCollection
          ? `Default browse collection is "${defaultCollection.name}". Omit collection_ids and omit scope for default browse. Use a personal collection only when the user named it. Pass search_slide_library with scope:"all" only when the user asks for all slides / the whole library.`
          : "No company default collection is available. search_slide_library with no collection_ids and no scope searches all insertable slides. Pass scope:\"all\" only when the user asks for all slides / the whole library. Personal collections are listed so you can resolve a named collection; do not pick one on your own.",
      },
    },
  };
}

export async function executeSearchSlideLibraryTool(
  call: ToolCall,
  options: { agentToolContext?: AgentToolContext } = {}
): Promise<{ result: ToolResult }> {
  const ctx = options.agentToolContext;
  try {
    return await executeSearchSlideLibraryToolInner(call, ctx);
  } catch (error) {
    return {
      result: {
        tool: "search_slide_library",
        success: false,
        error: catalogFetchErrorMessage(error, "search_slide_library"),
      },
    };
  }
}

async function executeSearchSlideLibraryToolInner(
  call: ToolCall,
  ctx: AgentToolContext | undefined
): Promise<{ result: ToolResult }> {
  const collections = await listSlideLibraryCollections(ctx?.companyId);
  const requestedIds = requestedCollectionIds(call);
  const offset = Math.max(0, Math.floor(call.offset ?? 0));
  const limit = Math.min(
    SLIDE_LIBRARY_SEARCH_MAX_LIMIT,
    Math.max(1, Math.floor(call.limit ?? SLIDE_LIBRARY_SEARCH_DEFAULT_LIMIT))
  );
  const defaultCollection = resolveCompanyDefaultCollection(collections);
  const wantAll =
    call.slideLibraryScope === "all" || (requestedIds.length === 0 && !defaultCollection);

  if (wantAll) {
    return searchAllInsertableSlides(call, collections, ctx, offset, limit, !defaultCollection && call.slideLibraryScope !== "all");
  }

  const resolved = resolveSearchCollections(collections, requestedIds);

  if (resolved.resolved.length === 0) {
    return {
      result: {
        tool: "search_slide_library",
        success: true,
        data: {
          collections: [],
          tags: call.tags ?? [],
          offset,
          limit,
          returned: 0,
          totalCount: 0,
          hasMore: false,
          nextOffset: null,
          items: [],
          ...(resolved.missingCollectionIds.length > 0
            ? { missingCollectionIds: resolved.missingCollectionIds }
            : {}),
          message: resolved.message,
        },
      },
    };
  }

  const settled = await allSettled(
    resolved.resolved.map(async (collection) => ({
      collection,
      slides: await fetchCollectionSlides(collection, ctx?.companyId),
    }))
  );
  const loaded: Array<{ collection: ResolvedSlideLibraryCollection; slides: SlideLibrarySlide[] }> = [];
  const failedCollectionIds: string[] = [];
  let firstFetchError: unknown;
  for (const [index, outcome] of settled.entries()) {
    if (outcome.status === "fulfilled") {
      loaded.push(outcome.value);
      continue;
    }
    failedCollectionIds.push(resolved.resolved[index].collection_id);
    firstFetchError ??= outcome.reason;
  }
  if (loaded.length === 0) {
    return {
      result: {
        tool: "search_slide_library",
        success: false,
        error: catalogFetchErrorMessage(firstFetchError, "search_slide_library"),
      },
    };
  }

  return pageMergedLibrarySearch({
    call,
    ctx,
    loaded,
    offset,
    limit,
    missingCollectionIds: resolved.missingCollectionIds,
    failedCollectionIds,
  });
}

async function searchAllInsertableSlides(
  call: ToolCall,
  collections: SlideLibraryCollectionListItem[],
  ctx: AgentToolContext | undefined,
  offset: number,
  limit: number,
  autoFallback: boolean
): Promise<{ result: ToolResult }> {
  const companyCollections = orderedCompanyCollections(collections);
  const companySettled = await allSettled(
    companyCollections.map(async (collection) => ({
      collection,
      slides: await fetchCollectionSlides(collection, ctx?.companyId),
    }))
  );
  const loaded: Array<{ collection: ResolvedSlideLibraryCollection; slides: SlideLibrarySlide[] }> = [];
  const failedCollectionIds: string[] = [];
  let firstCompanyError: unknown;
  for (const [index, outcome] of companySettled.entries()) {
    if (outcome.status === "fulfilled") {
      loaded.push(outcome.value);
      continue;
    }
    failedCollectionIds.push(companyCollections[index].collection_id);
    firstCompanyError ??= outcome.reason;
  }

  let personalSlides: SlideLibrarySlide[] | null = null;
  let personalError: unknown;
  try {
    personalSlides = await fetchAllSlidePages((page) =>
      fetchSlideLibrarySlides({ page, pageSize: SLIDE_PAGE_SIZE })
    );
  } catch (error) {
    personalError = error;
  }

  if (personalSlides && personalSlides.length > 0) {
    loaded.push({ collection: ALL_SLIDES_COLLECTION, slides: personalSlides });
  }

  if (loaded.length === 0) {
    if (companyCollections.length > 0) {
      return {
        result: {
          tool: "search_slide_library",
          success: false,
          error: catalogFetchErrorMessage(firstCompanyError ?? personalError, "search_slide_library"),
        },
      };
    }
    if (personalError) {
      return {
        result: {
          tool: "search_slide_library",
          success: false,
          error: personalLibraryFetchErrorMessage(personalError),
        },
      };
    }
    return {
      result: {
        tool: "search_slide_library",
        success: true,
        data: {
          scope: "all" as const,
          collections: [],
          tags: call.tags ?? [],
          offset,
          limit,
          returned: 0,
          totalCount: 0,
          hasMore: false,
          nextOffset: null,
          items: [],
          message:
            "This library has no READY slides. Build from scratch (FREEFORM) and tell the user.",
        },
      },
    };
  }

  return pageMergedLibrarySearch({
    call,
    ctx,
    loaded,
    offset,
    limit,
    failedCollectionIds,
    failedPersonalLibrary: Boolean(personalError),
    scope: "all",
    autoFallback,
  });
}

function stripImagePayload(raw: string): string {
  const trimmed = raw.trim();
  const comma = trimmed.indexOf(",");
  if (trimmed.startsWith("data:") && comma >= 0) {
    return trimmed.slice(comma + 1);
  }
  return trimmed;
}

async function fetchThumbnailBase64(slide: SlideLibrarySlide): Promise<string | null> {
  const src = buildLibraryThumbnailSrc(slide);
  if (!src) return null;
  if (src.startsWith("data:")) {
    return stripImagePayload(src);
  }
  const response = await fetch(src, withCredentials({ method: "GET" }));
  if (!response.ok) return null;
  const buffer = await response.arrayBuffer();
  if (buffer.byteLength === 0) return null;
  return arrayBufferToBase64(buffer);
}

async function isKnownInsertableSlide(
  slide: SlideLibrarySlide,
  ctx?: AgentToolContext
): Promise<boolean> {
  if (slide.collectionKind === "preslink_template") return false;
  if (slide.source !== "company") return isInsertableLibrarySlide(slide);
  if (!ctx?.companyId) return slide.collectionKind === "standard";
  try {
    const collections = await listSlideLibraryCollections(ctx.companyId);
    return collections.some(
      (collection) =>
        collection.collection_id === readSlideCollectionId(slide) &&
        collection.source === "company" &&
        collection.kind === "standard"
    );
  } catch {
    return false;
  }
}

async function findSlideById(
  slideId: string,
  ctx?: AgentToolContext
): Promise<SlideLibrarySlide | null> {
  const cached = ctx?.librarySlideCache?.get(slideId);
  if (cached && (await isKnownInsertableSlide(cached, ctx))) return cached;

  let collections: SlideLibraryCollectionListItem[] = [];
  try {
    collections = await listSlideLibraryCollections(ctx?.companyId);
  } catch {
    collections = [];
  }
  const defaultCollection = resolveCompanyDefaultCollection(collections);
  const ordered = [
    ...(defaultCollection ? [defaultCollection] : []),
    ...collections.filter((collection) => collection.collection_id !== defaultCollection?.collection_id),
  ];
  for (const collection of ordered) {
    let slides: SlideLibrarySlide[] = [];
    try {
      slides = await fetchCollectionSlides(
        {
          collection_id: collection.collection_id,
          name: collection.name,
          source: collection.source,
          isDefault: collection.isDefault,
          kind: collection.kind,
        },
        ctx?.companyId
      );
    } catch {
      continue;
    }
    for (const slide of slides) {
      if (readSlideId(slide) === slideId) {
        rememberLibrarySlide(ctx, slide);
        return slide;
      }
    }
  }

  try {
    const personal = await fetchSlideLibrarySlides({ page: 1, pageSize: 1000 });
    for (const slide of personal.slides) {
      if (readSlideId(slide) === slideId) {
        rememberLibrarySlide(ctx, slide);
        return slide;
      }
    }
  } catch {
    // Personal catalog is a last-resort lookup for an already-known slide id.
  }
  return null;
}

function libraryFieldsFromSlide(slide: SlideLibrarySlide): {
  title: string;
  thumbnailUrl?: string;
  description?: string;
  tags?: string[];
  pptxUrl?: string;
  pptxUrlCandidates?: string[];
} {
  const thumbnailUrl = buildLibraryThumbnailSrc(slide) ?? undefined;
  const pptxUrlCandidates = buildLibraryPptxSrcCandidates(slide);
  return {
    title: slide.title || "Library slide",
    ...(thumbnailUrl ? { thumbnailUrl } : {}),
    ...(slide.description ? { description: slide.description } : {}),
    ...(slide.tags && slide.tags.length > 0 ? { tags: slide.tags } : {}),
    ...(pptxUrlCandidates[0] ? { pptxUrl: pptxUrlCandidates[0] } : {}),
    ...(pptxUrlCandidates.length > 0 ? { pptxUrlCandidates } : {}),
  };
}

async function hydrateLibraryCandidate(
  candidate: LibrarySlideCandidate,
  ctx?: AgentToolContext
): Promise<LibrarySlideCandidate> {
  const slide = await findSlideById(candidate.slideId, ctx);
  if (!slide) return candidate;
  const fields = libraryFieldsFromSlide(slide);
  return {
    ...candidate,
    title: candidate.title.trim() ? candidate.title : fields.title,
    description: candidate.description || fields.description,
    tags: candidate.tags && candidate.tags.length > 0 ? candidate.tags : fields.tags,
    thumbnailUrl: candidate.thumbnailUrl || fields.thumbnailUrl,
    pptxUrl: candidate.pptxUrl || fields.pptxUrl,
    pptxUrlCandidates:
      candidate.pptxUrlCandidates && candidate.pptxUrlCandidates.length > 0
        ? candidate.pptxUrlCandidates
        : fields.pptxUrlCandidates,
  };
}

async function hydrateSlideOutlineLibraryFields(
  slide: SlideOutline,
  ctx?: AgentToolContext
): Promise<SlideOutline> {
  const next: SlideOutline = { ...slide };
  if (slide.librarySlideId) {
    const resolved = await findSlideById(slide.librarySlideId, ctx);
    if (resolved) {
      const fields = libraryFieldsFromSlide(resolved);
      next.librarySlideTitle = fields.title;
      next.librarySlideThumbnailUrl = fields.thumbnailUrl;
      next.librarySlideDescription = fields.description;
      next.librarySlideTags = fields.tags;
      next.librarySlidePptxUrl = fields.pptxUrl;
      next.librarySlidePptxUrlCandidates = fields.pptxUrlCandidates;
      next.rememberedLibrarySlide = {
        slideId: slide.librarySlideId,
        title: fields.title,
        description: fields.description,
        tags: fields.tags,
        thumbnailUrl: fields.thumbnailUrl,
        pptxUrl: fields.pptxUrl,
        pptxUrlCandidates: fields.pptxUrlCandidates,
      };
    }
  }
  if (slide.librarySlideCandidates && slide.librarySlideCandidates.length > 0) {
    next.librarySlideCandidates = await Promise.all(
      slide.librarySlideCandidates.map((candidate) => hydrateLibraryCandidate(candidate, ctx))
    );
  }
  return next;
}

export function planIsAllFreeform(plan: DeckPlan): boolean {
  return plan.slides.every(
    (slide) => !slide.librarySlideId && typeof slide.baseSlideIndex !== "number"
  );
}

export function shouldNudgeLibraryPicks(_plan: DeckPlan, _ctx?: AgentToolContext): boolean {
  return false;
}

export async function hydrateDeckPlanLibraryFields(
  plan: DeckPlan,
  ctx?: AgentToolContext
): Promise<DeckPlan> {
  const slides = await Promise.all(
    plan.slides.map((slide) => hydrateSlideOutlineLibraryFields(slide, ctx))
  );
  return { ...plan, slides };
}

const LIBRARY_PICK_NUDGE =
  "The slide library catalog has slides, but this plan has no librarySlideId on any slide. The Review carousel is showing Agent builds from scratch. Call update_deck_plan and set librarySlideId (and up to 3 librarySlideCandidates) on each planned slide from the catalog you already searched, unless the user asked for from-scratch or an in-deck duplicate.";

export function libraryPickNudgeMessage(): string {
  return LIBRARY_PICK_NUDGE;
}

export async function executeGetLibrarySlidePreviewsTool(
  call: ToolCall,
  options: { agentToolContext?: AgentToolContext } = {}
): Promise<{ result: ToolResult; referenceImages?: ReferenceSlideImage[] }> {
  const slideIds = (call.slideIds || []).slice(0, SLIDE_LIBRARY_SEARCH_MAX_LIMIT);
  const items: Array<{
    slide_id: string;
    title: string;
    description: string;
    tags: string[];
    hasPreview: boolean;
  }> = [];
  const referenceImages: ReferenceSlideImage[] = [];

  for (const slideId of slideIds) {
    const slide = await findSlideById(slideId, options.agentToolContext);
    if (!slide) {
      items.push({
        slide_id: slideId,
        title: "",
        description: "",
        tags: [],
        hasPreview: false,
      });
      continue;
    }
    rememberLibrarySlide(options.agentToolContext, slide);
    let hasPreview = false;
    try {
      const image = await fetchThumbnailBase64(slide);
      if (image) {
        referenceImages.push({ slideId, image });
        hasPreview = true;
      }
    } catch {
      hasPreview = false;
    }
    items.push({
      slide_id: slideId,
      title: slide.title || "Untitled slide",
      description: slide.description || "",
      tags: slide.tags || [],
      hasPreview,
    });
  }

  return {
    result: {
      tool: "get_library_slide_previews",
      success: true,
      data: {
        items,
        previewCount: referenceImages.length,
      },
    },
    referenceImages,
  };
}

export async function resolveLibrarySlidePptxSources(
  librarySlideId: string,
  ctx?: AgentToolContext
): Promise<{ title?: string; pptxSources: string[] } | null> {
  const slide = await findSlideById(librarySlideId, ctx);
  if (!slide) return null;
  const existing = ctx?.librarySlideSourcesById?.get(librarySlideId);
  if (existing && existing.pptxSources.length > 0) {
    return existing;
  }
  rememberLibrarySlide(ctx, slide);
  return ctx?.librarySlideSourcesById?.get(librarySlideId) ?? {
    title: slide.title,
    pptxSources: buildLibraryPptxSrcCandidates(slide),
  };
}
